import {
  applyMasterFilters,
  type Dial,
  type InstrumentFilters,
} from "./dials";
import {
  INSTRUMENT_PRESETS,
  QUICK_INSTRUMENTS,
  newCustomInstrument,
  sameInstrument,
} from "./eq";
import { DEFAULT_MIX_WEIGHTS, type MixWeights } from "./mixWeights";
import type { VocalDelivery, VocalProfile } from "./vocals";

export const AUTO_BOXES = [
  { id: "instruments", label: "Instruments" },
  { id: "frequency", label: "Frequencies" },
  { id: "weights", label: "Weights" },
  { id: "lyrics", label: "Lyrics" },
] as const;

export type AutoBoxId = (typeof AUTO_BOXES)[number]["id"];

export type AutoBoxes = Record<AutoBoxId, boolean>;

export const DEFAULT_AUTO_BOXES: AutoBoxes = {
  instruments: false,
  frequency: false,
  weights: false,
  lyrics: false,
};

export type MixRecipe = {
  id: string;
  label: string;
  genres: string[];
  instruments: Record<string, number>;
  extraInstruments: string[];
  frequency: InstrumentFilters;
  weights: Pick<MixWeights, "frequency" | "instruments" | "vocals" | "style" | "healing">;
  vocals: Record<string, number>;
  delivery: VocalDelivery;
};

export type AutoMixBank = "frequency" | "instruments" | "vocals";

const ACOUSTIC: MixRecipe = {
  id: "acoustic",
  label: "Acoustic / folk",
  genres: ["folk"],
  instruments: {
    kick: 32,
    snare: 38,
    hats: 22,
    bassinst: 55,
    keys: 48,
    guitar: 82,
    synth: 8,
    pads: 18,
    strings: 42,
    fx: 12,
  },
  extraInstruments: [],
  frequency: { hpf: 8, sub: 40, bass: 56, lowmid: 58, presence: 48, air: 36, lpf: 6 },
  weights: { frequency: 60, instruments: 82, vocals: 68, style: 48, healing: 0 },
  vocals: { vocal: 78, doubles: 28, harmony: 36, adlibs: 12, breath: 52, grit: 22, vibrato: 44 },
  delivery: "sung",
};

const ELECTRONIC: MixRecipe = {
  id: "electronic",
  label: "Electronic",
  genres: ["edm"],
  instruments: {
    kick: 78,
    snare: 62,
    hats: 68,
    bassinst: 72,
    keys: 32,
    guitar: 8,
    synth: 82,
    pads: 70,
    strings: 12,
    fx: 55,
  },
  extraInstruments: [],
  frequency: { hpf: 14, sub: 64, bass: 58, lowmid: 40, presence: 64, air: 70, lpf: 0 },
  weights: { frequency: 72, instruments: 76, vocals: 70, style: 60, healing: 0 },
  vocals: { vocal: 72, doubles: 48, harmony: 22, adlibs: 40, breath: 28, grit: 18, vibrato: 24 },
  delivery: "sung",
};

const BAND: MixRecipe = {
  id: "band",
  label: "Band",
  genres: ["rock"],
  instruments: {
    kick: 72,
    snare: 74,
    hats: 56,
    bassinst: 70,
    keys: 38,
    guitar: 80,
    synth: 18,
    pads: 22,
    strings: 12,
    fx: 28,
  },
  extraInstruments: [],
  frequency: { hpf: 6, sub: 54, bass: 62, lowmid: 52, presence: 62, air: 50, lpf: 0 },
  weights: { frequency: 66, instruments: 82, vocals: 76, style: 55, healing: 0 },
  vocals: { vocal: 80, doubles: 42, harmony: 30, adlibs: 28, breath: 36, grit: 52, vibrato: 32 },
  delivery: "sung",
};

const HEALING: MixRecipe = {
  id: "healing",
  label: "Healing",
  genres: ["healing", "ambient"],
  instruments: {
    kick: 8,
    snare: 4,
    hats: 6,
    bassinst: 22,
    keys: 42,
    guitar: 18,
    synth: 16,
    pads: 72,
    strings: 58,
    fx: 18,
  },
  extraInstruments: ["Singing bowls", "Harp"],
  frequency: { hpf: 0, sub: 56, bass: 48, lowmid: 44, presence: 40, air: 58, lpf: 8 },
  weights: { frequency: 52, instruments: 62, vocals: 42, style: 40, healing: 72 },
  vocals: { vocal: 55, doubles: 18, harmony: 40, adlibs: 8, breath: 60, grit: 6, vibrato: 50 },
  delivery: "sung",
};

const RAP: MixRecipe = {
  id: "rap",
  label: "Rap / hip-hop",
  genres: ["hip-hop"],
  instruments: {
    kick: 80,
    snare: 70,
    hats: 74,
    bassinst: 76,
    keys: 36,
    guitar: 10,
    synth: 48,
    pads: 40,
    strings: 8,
    fx: 42,
  },
  extraInstruments: [],
  frequency: { hpf: 16, sub: 70, bass: 64, lowmid: 38, presence: 58, air: 52, lpf: 0 },
  weights: { frequency: 68, instruments: 74, vocals: 82, style: 58, healing: 0 },
  vocals: { vocal: 86, doubles: 22, harmony: 18, adlibs: 55, breath: 24, grit: 40, vibrato: 12 },
  delivery: "rap",
};

const POP: MixRecipe = {
  id: "pop",
  label: "Pop",
  genres: ["pop"],
  instruments: {
    kick: 62,
    snare: 58,
    hats: 52,
    bassinst: 60,
    keys: 48,
    guitar: 36,
    synth: 44,
    pads: 46,
    strings: 28,
    fx: 32,
  },
  extraInstruments: [],
  frequency: { hpf: 10, sub: 50, bass: 54, lowmid: 48, presence: 60, air: 58, lpf: 0 },
  weights: { frequency: 58, instruments: 72, vocals: 74, style: 55, healing: 0 },
  vocals: { vocal: 78, doubles: 42, harmony: 38, adlibs: 28, breath: 40, grit: 18, vibrato: 36 },
  delivery: "sung",
};

function hay(bowl: string, genres: string[]): string {
  const b = (bowl || "").toLowerCase().trim();
  if (b) return b;
  return genres.join(" ").toLowerCase();
}

export function pickRecipe(bowl: string, genres: string[]): MixRecipe {
  const t = hay(bowl, genres);
  if (/heal|meditat|432|528|solfeg|drone|singing bowl/.test(t)) return withTextExtras(HEALING, t);
  if (/\brap\b|hip-?hop|trap verse/.test(t)) return withTextExtras(RAP, t);
  if (/synth|edm|house|electro|night drive|dubstep|techno|trance/.test(t)) {
    return withTextExtras(ELECTRONIC, t);
  }
  if (/rock|metal|punk|band|blues|garage/.test(t)) return withTextExtras(BAND, t);
  if (/folk|pub|tavern|acoustic|irish|country|drinking|campfire|fiddle|accordion|bagpipe/.test(t)) {
    const extra = [...ACOUSTIC.extraInstruments];
    if (/pub|tavern|irish|drinking/.test(t)) {
      for (const name of ["Accordion", "Fiddle"]) {
        if (!extra.includes(name)) extra.push(name);
      }
    }
    if (/bagpipe/.test(t) && !extra.includes("Bagpipes")) extra.push("Bagpipes");
    return withTextExtras({ ...ACOUSTIC, extraInstruments: extra }, t);
  }
  return withTextExtras(POP, t);
}

function withTextExtras(recipe: MixRecipe, text: string): MixRecipe {
  const extra = [...recipe.extraInstruments];
  for (const p of INSTRUMENT_PRESETS) {
    const hit = p.keys.some((k) => k.length > 2 && text.includes(k)) || text.includes(p.label.toLowerCase());
    if (hit && !extra.includes(p.label)) extra.push(p.label);
  }
  for (const name of QUICK_INSTRUMENTS) {
    if (text.includes(name.toLowerCase()) && !extra.includes(name)) extra.push(name);
  }
  return extra.length === recipe.extraInstruments.length
    ? recipe
    : { ...recipe, extraInstruments: extra };
}

function setDialValues(dials: Dial[], values: Record<string, number>): Dial[] {
  return dials.map((d) => {
    const next = values[d.id];
    return typeof next === "number" ? { ...d, value: next } : d;
  });
}

function ensureExtras(dials: Dial[], names: string[], makeId: (prefix: string) => string): Dial[] {
  let next = dials;
  for (const name of names) {
    if (next.some((d) => d.bank === "instruments" && sameInstrument(d.label, name))) continue;
    next = [...next, newCustomInstrument(name, makeId)];
  }
  return next;
}

function mergeGenres(current: string[], add: string[]): string[] {
  const out = [...current];
  for (const g of add) {
    if (!out.includes(g)) out.push(g);
  }
  return out;
}

export function applyAutoBox(input: {
  box: AutoBoxId;
  on: boolean;
  bowl: string;
  genres: string[];
  dials: Dial[];
  mixWeights: MixWeights;
  vocal: VocalProfile;
  uid: (prefix: string) => string;
}): {
  dials: Dial[];
  mixWeights: MixWeights;
  vocal: VocalProfile;
  genres: string[];
  openBank: AutoMixBank;
  status: string;
} {
  const recipe = pickRecipe(input.bowl, input.genres);
  if (input.box === "instruments") {
    if (!input.on) {
      return {
        dials: input.dials,
        mixWeights: { ...input.mixWeights, instruments: 0 },
        vocal: input.vocal,
        genres: input.genres,
        openBank: "instruments",
        status: "Auto sliders · Instruments off (lane weight 0). Tabs still hold the last levels.",
      };
    }
    const dials = ensureExtras(
      setDialValues(input.dials, recipe.instruments),
      recipe.extraInstruments,
      input.uid,
    );
    return {
      dials,
      mixWeights:
        input.mixWeights.instruments <= 0
          ? { ...input.mixWeights, instruments: recipe.weights.instruments }
          : input.mixWeights,
      vocal: input.vocal,
      genres: mergeGenres(input.genres, recipe.genres),
      openBank: "instruments",
      status: `Auto sliders · ${recipe.label} instruments`,
    };
  }
  if (input.box === "frequency") {
    if (!input.on) {
      return {
        dials: input.dials,
        mixWeights: { ...input.mixWeights, frequency: 0 },
        vocal: input.vocal,
        genres: input.genres,
        openBank: "frequency",
        status: "Auto sliders · Frequencies off (lane weight 0).",
      };
    }
    return {
      dials: applyMasterFilters(input.dials, recipe.frequency),
      mixWeights:
        input.mixWeights.frequency <= 0
          ? { ...input.mixWeights, frequency: recipe.weights.frequency }
          : input.mixWeights,
      vocal: input.vocal,
      genres: mergeGenres(input.genres, recipe.genres),
      openBank: "frequency",
      status: `Auto sliders · ${recipe.label} frequencies`,
    };
  }
  if (input.box === "weights") {
    if (!input.on) {
      return {
        dials: input.dials,
        mixWeights: {
          ...input.mixWeights,
          frequency: DEFAULT_MIX_WEIGHTS.frequency,
          instruments: DEFAULT_MIX_WEIGHTS.instruments,
        },
        vocal: input.vocal,
        genres: input.genres,
        openBank: "instruments",
        status: "Auto sliders · Weights reset to defaults for frequency and instruments.",
      };
    }
    return {
      dials: input.dials,
      mixWeights: {
        ...input.mixWeights,
        frequency: recipe.weights.frequency,
        instruments: recipe.weights.instruments,
        style: recipe.weights.style,
        healing: recipe.weights.healing,
      },
      vocal: input.vocal,
      genres: input.genres,
      openBank: "instruments",
      status: `Auto sliders · ${recipe.label} weights · instruments ${recipe.weights.instruments} · frequency ${recipe.weights.frequency}`,
    };
  }
  if (!input.on) {
    return {
      dials: input.dials,
      mixWeights: { ...input.mixWeights, vocals: 0 },
      vocal: { ...input.vocal, delivery: "instrumental" },
      genres: input.genres,
      openBank: "vocals",
      status: "Auto sliders · Lyrics off · instrumental mix.",
    };
  }
  return {
    dials: setDialValues(input.dials, recipe.vocals),
    mixWeights: { ...input.mixWeights, vocals: recipe.weights.vocals },
    vocal: { ...input.vocal, delivery: recipe.delivery, mic: input.vocal.mic || "close" },
    genres: mergeGenres(input.genres, recipe.genres),
    openBank: "vocals",
    status: `Auto sliders · Lyrics on · ${recipe.label} vocal lane`,
  };
}
